<template>
  <component
    :is="props.useClientLayout ? AdminLayout : PortalLayout"
    section="Alert Subscriptions"
    title="Alert Subscriptions Report"
    hideHeader
  >
    <div v-if="props.useClientLayout" class="pb-4">
      <BackButton @click="router.back()"> Back to Reports List </BackButton>
    </div>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">Alert Subscriptions Report</div>
        <div>
          {{ property?.name }}
        </div>
      </div>
    </h1>
    <div v-if="isPropertyLoading || isAlertSubsLoading">
      <Loading />
    </div>
    <div v-else>
      <div v-if="!alertSubscriptions || alertSubscriptions.length == 0">
        No people are subscribed for alerts.
      </div>
      <div v-else class="report-container mt-4">
        <div>
          <BaseButton class="float-right mb-2" @click="downloadExcel"> Export to Excel </BaseButton>
        </div>
        <table class="table-auto w-full mt-4 border-2 shadow text-sm">
          <thead class="font-sm font-bold">
            <tr>
              <td class="pl-3 py-2 cursor-pointer" @click="changeSort(SortColumn.FirstName)">
                First Name
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.FirstName" :isUp="!isSortingDown" />
              </td>
              <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.LastName)">
                Last Name
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.LastName" :isUp="!isSortingDown" />
              </td>
              <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Email)">
                Alert Email
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.Email" :isUp="!isSortingDown" />
              </td>
              <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Phone)">
                Alert Phone
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.Phone" :isUp="!isSortingDown" />
              </td>
              <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Push)">
                Push
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.Push" :isUp="!isSortingDown" />
              </td>
              <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Floors)">
                Alert Floors
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.Floors" :isUp="!isSortingDown" />
              </td>
              <td class="px-3 py-2 cursor-pointer" @click="changeSort(SortColumn.Role)">
                Role
                <UpDownArrow class="float-right" v-if="columnToSort == SortColumn.Role" :isUp="!isSortingDown" />
              </td>
            </tr>
          </thead>
          <tr
            v-for="(person, i) in sortedSubscriptions"
            :key="i"
            class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
            :class="i % 2 ? '' : 'bg-stone-200'"
          >
            <td class="pl-3">{{ person.firstName }}</td>
            <td class="pl-3">{{ person.lastName }}</td>
            <td class="pl-3">{{ person.contactEmail }}</td>
            <td class="pl-3" :class="person.isSmsOptedOut ? 'text-red-600' : ''">
              {{ person.contactSms }}{{ person.isSmsOptedOut ? " (Opted Out)" : "" }}
            </td>
            <td class="pl-3">
              {{ person.pushSubscriptionCount || "" }}
            </td>
            <td class="pl-3" :title="person.alertSubscribedFloors || 'All floors'">
              {{
                person.alertSubscribedFloors?.length > 10
                  ? person.alertSubscribedFloors.substring(0, 10) + "..."
                  : person.alertSubscribedFloors || "All floors"
              }}
            </td>
            <td class="px-3">
              {{ person.roleAlias }}
              <span v-if="getSiteRoleByName(person.roleAlias)?.isStaffRole" class="text-gray-500">
                (Staff)
              </span>
            </td>
          </tr>
        </table>
        <div>
          <BaseButton class="float-right mt-2" @click="downloadExcel"> Export to Excel </BaseButton>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { IContent, IJsonSheet } from "json-as-xlsx";
import * as jsonAsXlsx from "json-as-xlsx";
import PortalLayout from "../../layouts/PortalLayout.vue";
import AdminLayout from "../../layouts/AdminLayout.vue";
import { useBldgStore } from "../../store/bldgStore";
import { AlertPersonDTO } from "../../store/generatedFetchClient";
import { useAlertStore } from "../../store/alertStore";

const props = withDefaults(defineProps<{ useClientLayout: boolean }>(), {
  useClientLayout: false,
});

const router = useRouter();
const bldgStore = useBldgStore();
const alertStore = useAlertStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isAlertSubsLoading = ref(false);
var property = ref(bldgStore.building);

var alertSubscriptions = computed<AlertPersonDTO[]>(() => alertStore.alertSubscriptions);

//#region Sorting
enum SortColumn {
  FirstName,
  LastName,
  Email,
  Phone,
  Push,
  Floors,
  Role,
}

var columnToSort = ref<SortColumn>(SortColumn.LastName);
var isSortingDown = ref<boolean>(false);

function changeSort(column: SortColumn) {
  if (column == columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = false;
  }
}

var sortedSubscriptions = computed<AlertPersonDTO[]>(() => {
  let results = alertSubscriptions.value?.slice() ?? [];

  switch (columnToSort.value) {
    case SortColumn.FirstName:
      results = results.sort((a, b) => {
        if (a.firstName == b.firstName)
          return (a.lastName ?? "").localeCompare(b.lastName ?? "");
        return (a.firstName ?? "").localeCompare(b.firstName ?? "");
      });
      break;
    case SortColumn.LastName:
      results = results.sort((a, b) => {
        if (a.lastName == b.lastName)
          return (a.firstName ?? "").localeCompare(b.firstName ?? "");
        return (a.lastName ?? "").localeCompare(b.lastName ?? "");
      });
      break;
    case SortColumn.Email:
      results = results.sort((a, b) =>
        (a.contactEmail ?? "").localeCompare(b.contactEmail ?? "")
      );
      break;
    case SortColumn.Phone:
      results = results.sort((a, b) =>
        (a.contactSms ?? "").localeCompare(b.contactSms ?? "")
      );
      break;
    case SortColumn.Push:
      results = results.sort(
        (a, b) => (a.pushSubscriptionCount ?? 0) - (b.pushSubscriptionCount ?? 0)
      );
      break;
    case SortColumn.Floors:
      results = results.sort((a, b) =>
        (a.alertSubscribedFloors ?? "").localeCompare(b.alertSubscribedFloors ?? "")
      );
      break;
    case SortColumn.Role:
      results = results.sort((a, b) =>
        (a.roleAlias ?? "").localeCompare(b.roleAlias ?? "")
      );
      break;
  }

  if (isSortingDown.value) {
    results = results.reverse();
  }

  return results;
});
//#endregion

onMounted(loadData);
watch(property, loadData);

async function loadData() {
  isAlertSubsLoading.value = true;
  if (property?.value?.portalId) {
    await alertStore.getAlertSubscriptions(property.value.portalId);
  }
  isAlertSubsLoading.value = false;
}

function downloadExcel() {
  // Extract the xlsx function from the namespace import
  const xlsx = (jsonAsXlsx as any).default || (jsonAsXlsx as any).xlsx || jsonAsXlsx;

  let data: IJsonSheet[] = [
    {
      sheet: "Roster",
      columns: [
        {
          label: "First Name",
          value: "firstName",
        },
        {
          label: "Last Name",
          value: "lastName",
        },
        {
          label: "Alert Email",
          value: "alertEmail",
        },
        {
          label: "Alert Phone",
          value: "alertPhone",
        },
        {
          label: "Push Devices",
          value: "pushDevices",
        },
        {
          label: "Subscribed Floors",
          value: "subscribedFloors",
        },
        {
          label: "Role",
          value: "role",
        },
      ],
      content: [] as IContent[],
    },
  ];

  alertSubscriptions.value.map((result) => {
    data[0].content.push({
      firstName: result.firstName,
      lastName: result.lastName,
      alertEmail: result.contactEmail,
      alertPhone: result.contactSms,
      pushDevices: result.pushSubscriptionCount || 0,
      subscribedFloors: result.alertSubscribedFloors,
      role: result.roleAlias,
    });
  });

  xlsx(data, {
    fileName:
      "AlertSubscribers_" +
      property.value.name.replace(" ", "_") +
      "_" +
      new Date(Date.now()).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }),
  });
}

// #region Helpers

var getSiteRoleByName = (roleName: string) =>
  property.value?.siteRoles?.find((r) => r.roleAlias === roleName);

// #endregion Helpers
</script>
